import {
  GatewayAuthentication,
  GatewayControleQuinquennal,
} from "@conformite/gateway";
import ChevronDown from "@endya-conformite/ui-kit/src/assets/icons/chevron-down.svg?react";
import ChevronUp from "@endya-conformite/ui-kit/src/assets/icons/chevron-up.svg?react";
import Pencil from "@endya-conformite/ui-kit/src/assets/icons/pencil.svg?react";
import { Button, Card } from "@endya-conformite/ui-kit";
import { DownloadFile } from "@src/components/DownloadFile/DownloadFile";
import { useControleQuinquennalAdmin } from "@src/controleQuinquennal/admin/useControleQuinquennalAdmin";
import { replaceNBSP } from "@src/helper/clean.helper";
import { useIsAllowed } from "@src/session/useIsAllowed";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./ControleQuinquennalAxeRemediation.module.scss";

type ControleQuinquennalAxeRemediationProps = {
  remediation: GatewayControleQuinquennal.ControleQuinquennalRemediationWithFileResponseType;
  defaultOpen: boolean;
  siren: string;
  index: number;
  millesime: number;
  onDeleteRemediation: (idRemediation: string) => void;
};

const getFileExtension = (filename: string) => {
  const filenameParts = filename.split(".").pop() ?? "";
  if (["pdf", "docx", "xlsx"].includes(filenameParts))
    return `.${filenameParts}`;
  return "";
};

export function ControleQuinquennalAxeRemediation({
  defaultOpen,
  remediation,
  siren,
  index,
  millesime,
  onDeleteRemediation,
}: ControleQuinquennalAxeRemediationProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { hasAllowedCognitoRole } = useIsAllowed();
  const { axes } = useControleQuinquennalAdmin();
  const { idAxe } = useParams();

  const isCurrentAxeInRemediation =
    axes.find((axe) => axe.idAxe === idAxe)?.status ===
    GatewayControleQuinquennal.ControleQuinquennalAxeStatus.REMEDIE;

  return (
    <div className={styles.remediation}>
      <div
        className={styles.header}
        onClick={() => setIsOpen((old) => !old)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") setIsOpen((old) => !old);
        }}
      >
        <p className={styles.titleContainer}>
          <span className={styles.title}>Observations du contrôleur</span>
          <span>-</span>
          <span>
            {format(new Date(remediation.creationDate), "dd/MM/yyyy")}
          </span>
        </p>
        <Button
          variant="text"
          icon={isOpen ? <ChevronUp /> : <ChevronDown />}
        />
      </div>
      {isOpen && (
        <div>
          <p
            className={styles.description}
            // eslint-disable-next-line react/no-danger -- We sanitize the HTML
            dangerouslySetInnerHTML={{
              __html: replaceNBSP(remediation.description),
            }}
          />
          <div className={styles.filesWithActions}>
            <div className={styles.files}>
              {remediation.files.map((file) => (
                <div className={styles.file} key={file.idFile}>
                  <span className={styles.filename}>{file.filename}</span>
                  <DownloadFile
                    filename={`document_remediation_${new Date().toISOString()}${getFileExtension(
                      file.filename
                    )}`}
                    millesime={millesime}
                    siren={siren}
                    pathname={file.pathname}
                  />
                </div>
              ))}
            </div>
            {hasAllowedCognitoRole(GatewayAuthentication.Role.ENDYA) &&
              index === 0 &&
              isCurrentAxeInRemediation && (
                <div className={styles.actions}>
                  <Button
                    icon={<Pencil />}
                    variant="text"
                    onClick={() => {
                      navigate(`remediation/${remediation.id}`);
                    }}
                  >
                    Modifier le commentaire
                  </Button>
                  <Button
                    variant="text"
                    className={styles.deleteButton}
                    onClick={() => {
                      onDeleteRemediation(remediation.id);
                    }}
                  >
                    Supprimer
                  </Button>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
type ControleQuinquennalAxeRemediationsProps = {
  remediations: GatewayControleQuinquennal.ControleQuinquennalRemediationWithFileResponseType[];
  siren: string;
  millesime: number;
  onDeleteRemediation?: (idRemediation: string) => Promise<void>;
};
export function ControleQuinquennalAxeRemediations({
  remediations,
  siren,
  millesime,
  onDeleteRemediation,
}: ControleQuinquennalAxeRemediationsProps) {
  return (
    <Card className={styles.remediations}>
      {remediations.map((remediation, index) => (
        <ControleQuinquennalAxeRemediation
          remediation={remediation}
          key={remediation.id}
          defaultOpen={index === 0}
          siren={siren}
          index={index}
          millesime={millesime}
          onDeleteRemediation={(idRemediation) => {
            onDeleteRemediation?.(idRemediation)?.catch(console.error);
          }}
        />
      ))}
    </Card>
  );
}
